// The band arrangement, as one shared hook: everything that turns the store's
// chart into the ChordPlayer's playback — performance-order flattening (form
// cycling and detached codas included), voice-led guitar events, the walking
// bass with its per-pass variants, the comping track, the drum loop, the
// written-melody track, practice speed/trainer tempo, the live mid-playback
// option push, and playAll itself. Extracted from SequenceBuilderPage so the
// Score page can mount the SAME transport over the same machinery (unification
// Phase 2) — pages keep only their view concerns.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  chartToSong,
  chartToSequence,
  flatMeasuresToSequence,
  measureBeatGrid,
  measureStartBeats,
} from '@/music/chart';
import { flattenChart, passageMeasures } from '@/music/ireal';
import { restIncomingTies } from '@/music/chartMelody';
import type { SequenceChord } from '@/music/song';
import { optimizeFormCycle, type OptimizedChord } from '@/music/voiceLeading';
import { describeVoiceLeadingEngine, voiceLeadingEngine } from '@/music/voiceLeadingEngines';
import { melodyExposureByChord, voiceLeadPiano } from '@/music/pianoComping';
import { effectivePianoFamily } from '@/music/pianoLeftHand';
import { voiceByRuns } from '@/music/pianoRuns';
import { voiceLeadPianoMelody } from '@/music/pianoMelody';
import { pitchClassOf } from '@/music/notes';
import {
  applyBeatTwoAnswers,
  generateComping,
  generateSegmentedComping,
  type CompSegment,
} from '@/music/comping';
import { buildArrangementVoicings } from '@/music/notation/arrangement';
import { pianoPinnedVoicing, pinKeyResolver, stampCompingPins } from '@/music/voicingPins';
import {
  embellishBassLine,
  formFeelAt,
  generateWalkingBass,
  confineBassToChords,
} from '@/music/walkingBass';
import { compFeelForBand, describeBandPreset, STYLE_MODIFIED, type StyleBand } from '@/music/style';
import { feelAtBeat, feelSpans, hasFeelCues } from '@/audio/feelSpans';
import { feelsInScore } from '@/music/arrangement/fromCues';
import { scopeLabelText, type BandSplit } from '@/components/bandScope';
import { detectSections, sectionCueMeasures } from '@/music/sectionPlan';
import { mergeSections, sectionPlaysMelody } from '@/state/tunePlayback';
import { catalogueFeelBands } from '@/storage/grooveCatalogue';
import { catalogueCompPatternRanges } from '@/storage/compPatternCatalogue';
import { tuneArrangementOf, useStore, type VoicingLens } from '@/state/store';
import {
  getChordPlayer,
  type ArrangementOptions,
  type AuthoredCompSpan,
  type BassTrackNote,
} from '@/audio/player';
import { accentBassLine, type FeelPlacement } from '@/audio/feel';
import { phraseMelody } from '@/audio/phrasing';
import { withBandFeel } from '@/music/bandFeel';
import { swingGrid, type SwingOffsets } from '@/music/swingFeel';
import { drumLoopURL, loopsForMeter } from '@/audio/drumLoops';
import { useDrumLoops } from '@/data/uploadedLoops';
import { useSequencePlaying, useTransportState } from '@/audio/useSequencePlaying';
import { usePlaybackBeat, usePlaybackPass } from '@/audio/usePlaybackBeat';
import { trainerPctForPass } from '@/audio/practice';
import { playbackStatus } from '@/components/playbackStatus';
import { buildSequencePlaybackForm } from '@/pages/sequencePlayback';
import { loopingPassPosition, loopStartPassIndex } from './playbackPasses';
import { passageMeasureIds, passageRange } from '@/music/passage';
import { playedOccurrence, writtenMeasureId } from '@/audio/playCursor';
import {
  extendNotesForHolds,
  fermataHoldWindows,
  sustainCompHitsForHolds,
  thinBassForHolds,
  HOLD_STRETCH,
} from '@/audio/holds';
import { lastBarsWindow } from '@/audio/endings';
import {
  buildAuthoredPartPlayback,
  melodyPassSpans,
  mergeGeneratedWithAuthored,
  restGeneratedIn,
  type PartPlaybackOwnership,
  type PerformanceMeasureOccurrence,
} from '@/music/score/partPlayback';
import { buildBandCuePlayback } from '@/music/score/bandCues';
import { buildScoreExpression } from '@/music/score/expression';
import { chartMelodyPlayback, withMelodyExpression } from '@/music/score/melodyPlayback';
import { withPartPlaybackRules, withPartPlaybackSources } from '@/music/score/playbackRules';
import {
  BUILTIN_ALTERNATIVE_SHAPES,
  DEFAULT_PREFERRED_ALTERNATIVE_IDS,
  type AlternativeShape,
} from '@/music/alternativeShapes';

export type OptimizedSeqChord = OptimizedChord<SequenceChord>;

/** Most passes the form-cycle will write out before giving up on closure. */
export const CYCLE_MAX_PASSES = 4;

/**
 * Map a bar index of a pickupOnce whole-form-repeated flatten (the cycled
 * form: `flattenChart(chart, { wholeRepeats, pickupOnce: true })`) back onto
 * the single-pass index. Later passes omit the pickup bar, so a plain modulo
 * drifts one bar behind per pass on pickup tunes — the playhead washed the
 * PREVIOUS bar for the whole of pass 2. Identity for any index inside the
 * first pass, so it is safe on un-cycled forms too.
 */
export function singlePassIndexOf(
  index: number,
  singleFlat: readonly { pickupBeats?: number }[],
): number {
  if (singleFlat.length === 0) return 0;
  return singleFlat[0]?.pickupBeats != null && singleFlat.length > 1
    ? index === 0
      ? 0
      : 1 + ((index - 1) % (singleFlat.length - 1))
    : index % singleFlat.length;
}

/** The last bar starting at or before `beat` on a bar-start grid, or −1. */
function barAtBeat(grid: readonly number[], beat: number): number {
  let idx = -1;
  for (let i = 0; i < grid.length; i++) {
    if (grid[i] <= beat) idx = i;
    else break;
  }
  return idx;
}

/**
 * Whether a workbench lens applies the form cycle. Only the comping lens shows
 * the Cycle form row and its verdict; on any other lens a persisted cycleForm
 * is a hidden setting nobody can see or turn off, and cycling would still
 * switch off the itinerary, the endings and the chorus skips.
 */
export function lensCyclesForm(lens: VoicingLens): boolean {
  return lens === 'comping';
}

export type BandArrangement = ReturnType<typeof useBandArrangement>;

export interface UseBandArrangementOptions {
  /** Sound the melody-led grips shown by Arrange instead of the ordinary
   * voice-leading line. The source harmony still drives bass and analysis. */
  arrangePlayback?: boolean;
  /** Apply the comping-only form-cycle transform. Only a view that shows the
   * Cycle form control should pass true: chord melody supplies its own
   * melody-led line, and no other view may inherit a hidden cycle setting. */
  formCycleEnabled?: boolean;
  /** Saved alternatives supplement ordinary family candidates. Guided studies
   * pass an empty list to preserve the selected drop-voicing recipe. */
  alternativeShapes?: readonly AlternativeShape[];
  preferredAlternativeIds?: readonly string[];
}

/** Keep written silence as well as written notes when deciding who owns a channel.
 * With `sounding`, only the bars whose written part plays a note count. */
function authoredPartSpans(
  ownership: PartPlaybackOwnership[],
  sounding?: ReadonlySet<number>,
): AuthoredCompSpan[] {
  const spans: AuthoredCompSpan[] = [];
  for (const occurrence of ownership) {
    if (!occurrence.owned || occurrence.beats <= 1e-9) continue;
    if (sounding && !sounding.has(occurrence.occurrenceIndex)) continue;
    const previous = spans[spans.length - 1];
    const previousEnd = previous ? previous.startBeat + previous.durationBeats : -Infinity;
    if (previous && occurrence.startBeat <= previousEnd + 1e-9) {
      previous.durationBeats =
        Math.max(previousEnd, occurrence.startBeat + occurrence.beats) - previous.startBeat;
    } else {
      spans.push({ startBeat: occurrence.startBeat, durationBeats: occurrence.beats });
    }
  }
  return spans;
}

export function useBandArrangement({
  arrangePlayback = false,
  formCycleEnabled = true,
  alternativeShapes = BUILTIN_ALTERNATIVE_SHAPES,
  preferredAlternativeIds = DEFAULT_PREFERRED_ALTERNATIVE_IDS,
}: UseBandArrangementOptions = {}) {
  const savedChart = useStore((s) => s.chart);
  const score = useStore((s) => s.score);
  // The chart projects the lead-role part or first legacy untagged part.
  const melodyPart =
    score.parts.find((part) => part.role === 'lead') ??
    score.parts.find((part) => part.role === undefined);
  const expressionByEvent = useMemo(() => buildScoreExpression(score), [score]);
  const chart = useMemo(
    () => withMelodyExpression(withPartPlaybackSources(melodyPart, savedChart), expressionByEvent),
    [melodyPart, savedChart, expressionByEvent],
  );
  const stringSet = useStore((s) => s.stringSet);
  const voicingFamily = useStore((s) => s.voicingFamily);
  const chordMelodyDensity = useStore((s) => s.chordMelodyDensity);
  const freeStringSet = useStore((s) => s.freeStringSet);
  const avoidB9 = useStore((s) => s.avoidB9);
  const audioEnabled = useStore((s) => s.audioEnabled);
  const startingInversion = useStore((s) => s.startingInversion);
  const engineId = useStore((s) => s.voiceLeadingEngine);
  const engine = voiceLeadingEngine(engineId);
  // Your own grips for this tune (voicingPins.ts), by lane: the engine takes
  // each as the whole candidate set for its chord and fits the rest around it.
  const pins = useStore((s) => tuneArrangementOf(s)?.pins);
  const engineDiagnostic = describeVoiceLeadingEngine(engineId);
  const tempo = useStore((s) => s.tempo);
  const metronome = useStore((s) => s.metronome);
  const countIn = useStore((s) => s.countIn);
  const countInBars = useStore((s) => s.countInBars);
  const countInBetweenLoops = useStore((s) => s.countInBetweenLoops);
  const speedPct = useStore((s) => s.speedPct);
  const trainer = useStore((s) => s.trainer);
  const repeatForm = useStore((s) => s.repeatForm);
  const ending = useStore((s) => s.ending);
  const patchEnding = useStore((s) => s.patchEnding);
  const arrangementPolicy = useStore((s) => s.arrangementPolicy);
  const setRepeatForm = useStore((s) => s.setRepeatForm);
  const setChartRepeats = useStore((s) => s.setChartRepeats);
  const cycleForm = useStore((s) => s.cycleForm);
  const passage = useStore((s) => s.passage);
  const compStyle = useStore((s) => s.compStyle);
  const compInstrument = useStore((s) => s.compInstrument);
  const diagramInstrument = useStore((s) => s.diagramInstrument);
  const compPianoFamily = useStore((s) => s.compPianoFamily);
  const compAmount = useStore((s) => s.compAmount);
  const drums = useStore((s) => s.drums);
  const drumLoopKey = useStore((s) => s.drumLoopKey);
  const drumTimeStretch = useStore((s) => s.drumTimeStretch);
  const followFeelCues = useStore((s) => s.followFeelCues);
  const currentTuneKey = useStore((s) => s.currentTuneKey);
  const allTuneFeels = useStore((s) => s.tuneFeels);
  const houseFeels = useStore((s) =>
    s.currentTuneKey ? s.houseDefaults[s.currentTuneKey]?.settings.feels : undefined,
  );
  const houseSections = useStore((s) =>
    s.currentTuneKey ? s.houseDefaults[s.currentTuneKey]?.settings.sections : undefined,
  );
  const ownSections = useStore((s) =>
    s.currentTuneKey ? s.tunePlayback[s.currentTuneKey]?.sections : undefined,
  );
  const bassline = useStore((s) => s.bassline);
  const melodyChannel = useStore((s) => s.melodyChannel);
  const leadSound = useStore((s) => s.leadSound);
  const bassSound = useStore((s) => s.bassSound);
  const guitarSound = useStore((s) => s.guitarSound);
  const bassSolo = useStore((s) => s.bassSolo);
  const bassStyle = useStore((s) => s.bassStyle);
  const bassFeel = useStore((s) => s.bassFeel);
  const bassFollowForm = useStore((s) => s.bassFollowForm);
  const bassSwing = useStore((s) => s.bassSwing);
  const swingFeel = useStore((s) => s.swingFeel);
  const swingWithKit = useStore((s) => s.swingWithKit);
  const melodyPlacement = useStore((s) => s.melodyPlacement);
  const melodyPhrasing = useStore((s) => s.melodyPhrasing);
  const bassPlacement = useStore((s) => s.bassPlacement);
  const bassAccent = useStore((s) => s.bassAccent);
  const bassSkips = useStore((s) => s.bassSkips);
  const compPlacement = useStore((s) => s.compPlacement);
  const swingOffset = useStore((s) => s.swingOffset);
  const melodySwingOffset = useStore((s) => s.melodySwingOffset);
  const compSwingOffset = useStore((s) => s.compSwingOffset);
  const bassSwingOffset = useStore((s) => s.bassSwingOffset);
  const feelTiming = useStore((s) => s.feelTiming);
  const bassGhosts = useStore((s) => s.bassGhosts);
  const bassAnticipate = useStore((s) => s.bassAnticipate);
  const bassTriplets = useStore((s) => s.bassTriplets);
  const bassAmount = useStore((s) => s.bassAmount);
  // scoreToChart projects the lead-role part or the first legacy untagged
  // part. Preserve an imported piano/bass/guitar's instrument on that path.
  const melodyInstrument =
    melodyPart?.instrument === 'piano'
      ? 'piano'
      : melodyPart?.instrument === 'bass' || melodyPart?.role === 'bass'
        ? 'bass'
        : melodyPart?.instrument === 'guitar' ||
            (melodyPart?.instrument === 'other' && melodyPart.role !== 'lead')
          ? 'guitar'
          : 'lead';
  // The LEFT-HAND comp does not decide the lead (user ruling, Sep 2026: "you
  // will often comp a lead with left hand too"): the trio is the Melody
  // channel's Sound set to Piano, and a horn over a left-hand comp is as
  // ordinary — and the left-hand voicings read as charts either way.

  // Keep the shared band's voices in sync with the mixer before Play, and
  // warm them without taking ownership of a transport that is already live.
  useEffect(() => {
    const player = getChordPlayer();
    player.setLeadSound(leadSound);
    player.setBassSound(bassSound);
    player.setGuitarSound(guitarSound);
    if (audioEnabled) void player.preloadInstrumentSamples();
  }, [leadSound, bassSound, guitarSound, audioEnabled]);

  const sequencePlaying = useSequencePlaying();
  const transport = useTransportState();
  // The bundled loops and, for a member with access, the uploaded ones (#430).
  const drumLoops = useDrumLoops();
  const currentStyle = describeBandPreset({
    bassline,
    bassSolo,
    bassStyle,
    bassFeel,
    bassFollowForm,
    bassSwing,
    swingFeel,
    swingWithKit,
    melodyPlacement,
    melodyPhrasing,
    bassPlacement,
    bassAccent,
    bassSkips,
    compPlacement,
    swingOffset,
    melodySwingOffset,
    compSwingOffset,
    bassSwingOffset,
    bassGhosts,
    bassAnticipate,
    bassTriplets,
    bassAmount,
    compInstrument,
    compStyle,
    compAmount,
    drums,
    drumLoopKey,
  });
  const currentStyleId = currentStyle?.id ?? null;
  const currentStyleModified = currentStyle?.modified ?? false;
  const currentStyleLabel = currentStyle
    ? `${currentStyle.label}${currentStyleModified ? STYLE_MODIFIED : ''}`
    : 'Custom';

  // Performance order (repeats/endings expanded) → optimiser + diagrams.
  const singleFlat = useMemo(() => flattenChart(chart), [chart]);

  // Pin keys are WRITTEN positions: bar index in the chart + onset beat, so a
  // repeated bar's every pass shares its pin and a re-parsed library tune
  // (fresh random ids on every load) finds its pins again.
  const pinKeyOf = useMemo(
    () => pinKeyResolver(chart.measures.map((measure) => measure.id)),
    [chart.measures],
  );
  const sequence = useMemo(
    () => stampCompingPins(chartToSequence(chart, { stringSet }), pins, pinKeyOf),
    [chart, stringSet, pins, pinKeyOf],
  );

  // A pickup is the way into the tune, not part of the cyclic body. Form
  // cycling therefore starts its state machine on the first full bar. This
  // matters even when the pickup carries harmony: the playback policy keeps
  // the band silent there, so it must not influence the next-pass voicing.
  const cycleMeasures = useMemo(
    () => (singleFlat[0]?.pickupBeats != null ? singleFlat.slice(1) : singleFlat),
    [singleFlat],
  );
  const cycleSequence = useMemo(
    () =>
      stampCompingPins(
        flatMeasuresToSequence(cycleMeasures, stringSet, chart.timeSignature),
        pins,
        pinKeyOf,
      ),
    [cycleMeasures, stringSet, chart.timeSignature, pins, pinKeyOf],
  );

  // Form cycle: repeat the whole form, voice-leading each pass's first chord
  // from the previous pass's ending, until the opening voicing returns (A → A′
  // → …). The greedy optimiser is Markovian, so optimising the form repeated
  // N+1 times IS that chain; optimizeFormCycle finds where it closes.
  const cycle = useMemo(() => {
    if (!formCycleEnabled || !cycleForm || cycleSequence.length === 0) return null;
    try {
      const repeatedMeasures = flattenChart(chart, {
        wholeRepeats: CYCLE_MAX_PASSES + 1,
        pickupOnce: true,
      }).slice(singleFlat[0]?.pickupBeats != null ? 1 : 0);
      const repeated = stampCompingPins(
        flatMeasuresToSequence(repeatedMeasures, stringSet, chart.timeSignature),
        pins,
        pinKeyOf,
      );
      return optimizeFormCycle(
        repeated,
        cycleSequence.length,
        {
          startingInversion,
          avoidB9,
          freeStringSet,
          voicingFamily,
          alternativeShapes,
          preferredAlternativeIds,
        },
        CYCLE_MAX_PASSES,
      );
    } catch {
      return null;
    }
  }, [
    cycleForm,
    formCycleEnabled,
    chart,
    stringSet,
    singleFlat,
    cycleSequence.length,
    startingInversion,
    avoidB9,
    freeStringSet,
    voicingFamily,
    alternativeShapes,
    preferredAlternativeIds,
    pins,
    pinKeyOf,
  ]);
  const cycleActive = !!cycle && cycle.passes > 1;

  // The effective performance order: the cycled form when cycling, else one pass.
  const flat = useMemo(
    () =>
      cycleActive && cycle
        ? flattenChart(chart, { wholeRepeats: cycle.passes, pickupOnce: true })
        : singleFlat,
    [cycleActive, cycle, chart, singleFlat],
  );
  const barStartBeats = useMemo(
    () => measureStartBeats(flat, chart.timeSignature),
    [flat, chart.timeSignature],
  );

  const optimized = useMemo(() => {
    // Cycling always rides the greedy chain (its Markov property is what the
    // cycle machinery is built on); otherwise the chosen ENGINE voices the line.
    if (cycleActive && cycle) {
      const pickupOffset = singleFlat[0]?.pickupBeats != null ? 1 : 0;
      return cycle.chords.map((chord) => ({
        ...chord,
        barIndex: chord.barIndex + pickupOffset,
      })) as OptimizedSeqChord[];
    }
    if (sequence.length === 0) return null;
    try {
      return engine.run(sequence, {
        startingInversion,
        avoidB9,
        freeStringSet,
        voicingFamily,
        alternativeShapes,
        preferredAlternativeIds,
      });
    } catch {
      return null;
    }
  }, [
    cycleActive,
    cycle,
    singleFlat,
    sequence,
    startingInversion,
    avoidB9,
    freeStringSet,
    voicingFamily,
    alternativeShapes,
    preferredAlternativeIds,
    engine,
  ]);

  // Bare paired codas have two performance shapes: repeat the regular chorus,
  // then take the detached tail only on the final pass. Keep the engraved view
  // unchanged; build a separate, already-expanded playback/optimisation form.
  const playbackForm = useMemo(
    () =>
      buildSequencePlaybackForm(chart, flat, {
        repeatForm,
        cycleActive,
        policy: arrangementPolicy,
        ending,
      }),
    [chart, flat, repeatForm, cycleActive, arrangementPolicy, ending],
  );
  // What the playback form is built from BESIDE the Ending settings. An Ending
  // change leaves this identity alone, which is how the two stop effects below
  // tell it (the player takes it live: every chorus but the last is the same
  // music) from a policy switch, a chorus count or a form edit, which stop so
  // the next Play takes one coherent score.
  const formBasis = useMemo(
    () => ({ chart, flat, repeatForm, cycleActive, arrangementPolicy }),
    [chart, flat, repeatForm, cycleActive, arrangementPolicy],
  );
  const loopSlice = useMemo(() => {
    if (!passage) return null;
    const range = passageRange(
      chart.measures.map((measure) => measure.id),
      passage,
    );
    if (!range) return null;
    // Read in the whole form's context: a loop opening on a simile or on a bar
    // that carries the previous chord still plays that chord.
    const slice = passageMeasures(chart.measures, range.from, range.to, chart.timeSignature);
    if (slice.length === 0) return null;
    return restIncomingTies(slice);
  }, [passage, chart.measures, chart.timeSignature]);
  const loopingRegion = !!loopSlice;
  const loopKey =
    loopingRegion && passage ? `${passage.fromMeasureId}\u0000${passage.toMeasureId}` : '';
  const prevLoopKeyRef = useRef(loopKey);
  const detachedPlayback = playbackForm.preExpanded || loopingRegion;
  const playbackFlat = loopSlice ?? playbackForm.measures;
  const playbackPassStarts = loopingRegion ? undefined : playbackForm.passStarts;
  const playbackSequence = useMemo(
    () =>
      detachedPlayback
        ? stampCompingPins(
            flatMeasuresToSequence(playbackFlat, stringSet, chart.timeSignature),
            pins,
            pinKeyOf,
          )
        : sequence,
    [detachedPlayback, playbackFlat, stringSet, chart.timeSignature, sequence, pins, pinKeyOf],
  );
  const playbackOptimized = useMemo(() => {
    if (!detachedPlayback) return optimized;
    if (playbackSequence.length === 0) return null;
    try {
      return engine.run(playbackSequence, {
        startingInversion,
        avoidB9,
        freeStringSet,
        voicingFamily,
        alternativeShapes,
        preferredAlternativeIds,
      });
    } catch {
      return null;
    }
  }, [
    detachedPlayback,
    optimized,
    playbackSequence,
    startingInversion,
    avoidB9,
    freeStringSet,
    voicingFamily,
    alternativeShapes,
    preferredAlternativeIds,
    engine,
  ]);
  const playbackBarStartBeats = useMemo(
    () => (detachedPlayback ? measureStartBeats(playbackFlat, chart.timeSignature) : barStartBeats),
    [detachedPlayback, playbackFlat, chart.timeSignature, barStartBeats],
  );
  // Each played bar's WRITTEN bar index (undefined for a bar the tune does not
  // write, like an added resolution bar).
  const playedWritten = useMemo(() => {
    const formIndex = new Map(score.form.map((measure, index) => [measure.id, index]));
    return playbackFlat.map(
      (measure, index) =>
        formIndex.get(measure.id) ??
        // Form-cycle analysis clones the already-flattened chorus with fresh
        // measure ids. Its order is still a strict whole-form repetition, so
        // recover the written source by position; otherwise every authored
        // part silently disappears while cycle mode is active.
        (cycleActive && singleFlat.length > 0
          ? formIndex.get(singleFlat[singlePassIndexOf(index, singleFlat)].id)
          : undefined),
    );
  }, [score.form, playbackFlat, cycleActive, singleFlat]);
  // The same map for the SHEET's form (the rolled or cycled line the diagrams
  // draw), so its keyboards read each section's voicings as playback does.
  const sheetWritten = useMemo(() => {
    const formIndex = new Map(score.form.map((measure, index) => [measure.id, index]));
    return flat.map(
      (measure, index) =>
        formIndex.get(measure.id) ??
        (cycleActive && singleFlat.length > 0
          ? formIndex.get(singleFlat[singlePassIndexOf(index, singleFlat)].id)
          : undefined),
    );
  }, [score.form, flat, cycleActive, singleFlat]);
  const playbackOccurrences = useMemo(() => {
    let meter = chart.timeSignature;
    const out: PerformanceMeasureOccurrence[] = [];
    // Which itinerary pass a beat falls in (head / solo / head-out), so parts
    // written for the heads can yield the solo choruses to the generated band.
    const kinds = playbackForm.passKinds;
    const passKindAt = (beat: number) => {
      if (!kinds || !playbackPassStarts) return undefined;
      let kind: (typeof kinds)[number] | undefined;
      playbackPassStarts.forEach((start, index) => {
        if (start <= beat + 1e-9) kind = kinds[index];
      });
      return kind;
    };
    playbackFlat.forEach((measure, index) => {
      meter = measure.timeSig ?? meter;
      const formMeasureIndex = playedWritten[index];
      if (formMeasureIndex === undefined) return;
      const startBeat = playbackBarStartBeats[index] ?? 0;
      const nextStart = playbackBarStartBeats[index + 1];
      const passKind = passKindAt(startBeat);
      out.push({
        formMeasureIndex,
        startBeat,
        beats: nextStart === undefined ? (measure.pickupBeats ?? meter[0]) : nextStart - startBeat,
        ...(passKind ? { passKind } : {}),
      });
    });
    return out;
  }, [
    chart.timeSignature,
    playbackFlat,
    playbackBarStartBeats,
    playedWritten,
    playbackForm.passKinds,
    playbackPassStarts,
  ]);
  const authoredBass = useMemo(
    () => buildAuthoredPartPlayback(score, 'bass', playbackOccurrences),
    [score, playbackOccurrences],
  );
  const bandCues = useMemo(
    () => buildBandCuePlayback(score, playbackOccurrences),
    [score, playbackOccurrences],
  );
  const authoredComp = useMemo(
    () => buildAuthoredPartPlayback(score, 'comp', playbackOccurrences),
    [score, playbackOccurrences],
  );
  const authoredCompSpans = useMemo(
    () => authoredPartSpans(authoredComp.ownership),
    [authoredComp.ownership],
  );
  const authoredBassSpans = useMemo(
    () => authoredPartSpans(authoredBass.ownership),
    [authoredBass.ownership],
  );
  // The bars a written bass line plays a note in: the kit keeps time there
  // even where the chart writes N.C. (Armando's Rumba's head is written parts
  // over N.C.), where a bar with no chord is otherwise a band break.
  const writtenBassPlays = useMemo(
    () =>
      authoredPartSpans(
        authoredBass.ownership,
        new Set(authoredBass.notes.map((note) => note.occurrenceIndex)),
      ),
    [authoredBass],
  );
  const formBeats = useMemo(
    () =>
      playbackOccurrences.reduce(
        (end, occurrence) => Math.max(end, occurrence.startBeat + occurrence.beats),
        0,
      ),
    [playbackOccurrences],
  );
  const metronomeBeats = useMemo(
    () => measureBeatGrid(playbackFlat, chart.timeSignature),
    [playbackFlat, chart.timeSignature],
  );
  // A leading anacrusis bar: melody-only in playback — the band (and the drum
  // loop's bar grid) enters at the first full bar, and a count-in overlays it.
  const pickupBeats = playbackFlat[0]?.pickupBeats ?? 0;
  // Entry material past the pickup (a written intro/verse): the player's loop
  // re-enters here, so ×N and ∞ repeat the chorus, never the way in. A
  // passage loop overrides the whole form, entry included.
  const loopStartBeat = loopingRegion ? undefined : playbackForm.loopStartBeat;

  // A pre-expanded playback form is SNAPSHOTTED into the player at Play — the
  // event array and pass grid cannot be remapped live. If the itinerary is
  // rebuilt mid-playback (policy switch, chorus-count change, a form edit),
  // stop the transport instead of pushing tracks laid on a different grid
  // under the running events; the next Play uses the new form. The shape is
  // the bar-id sequence, so a metadata edit (or a rebuild to identical bars,
  // e.g. as-written ↔ ×2 policy on a D.S. tune) never interrupts playback.
  // A loop-range change is the exception: keep sounding and start the new
  // slice (see the restart effect on `loopKey`). Idle/paused selection only
  // arms the range — it must not press Play.
  const playbackShape = useMemo(() => playbackFlat.map((m) => m.id).join('\u0000'), [playbackFlat]);
  const playbackShapeRef = useRef({
    shape: playbackShape,
    preExpanded: detachedPlayback,
    basis: formBasis,
    ending,
  });
  useEffect(() => {
    const prev = playbackShapeRef.current;
    playbackShapeRef.current = {
      shape: playbackShape,
      preExpanded: detachedPlayback,
      basis: formBasis,
      ending,
    };
    if (prevLoopKeyRef.current !== loopKey) return;
    // An Ending change is handed over live (setArrangementForm, below).
    if (prev.basis === formBasis && prev.ending !== ending) return;
    if (prev.shape !== playbackShape && sequencePlaying && (detachedPlayback || prev.preExpanded)) {
      getChordPlayer().stop();
    }
  }, [playbackShape, detachedPlayback, sequencePlaying, loopKey, formBasis, ending]);

  // Fermata bars hold: the band strikes together and time stretches. The
  // windows silence clicks/drums in the player and thin the tracks below.
  // ENDINGS (src/audio/endings.ts) apply to a form that ENDS: never under ∞
  // or a passage loop, and cycling rewrites the form itself. The playback
  // form above has already written a looping form out when one is on, so the
  // final bars exist exactly once and these windows are the tune's ending.
  const endingAvailable = !repeatForm && !loopingRegion && !cycleActive;
  const holds = useMemo(() => {
    const written = fermataHoldWindows(playbackFlat, chart.timeSignature);
    if (!endingAvailable || !ending.holdLast) return written;
    const last = lastBarsWindow(playbackFlat, chart.timeSignature, 1);
    if (!last) return written;
    // A written fermata on the last bar already holds it.
    if (written?.some((w) => Math.abs(w.start - last.start) < 1e-6)) return written;
    return [...(written ?? []), last];
  }, [playbackFlat, chart.timeSignature, endingAvailable, ending.holdLast]);
  const ritardando = useMemo(
    () =>
      endingAvailable && ending.ritardandoBars > 0
        ? lastBarsWindow(playbackFlat, chart.timeSignature, ending.ritardandoBars)
        : undefined,
    [playbackFlat, chart.timeSignature, endingAvailable, ending.ritardandoBars],
  );
  const authoredCompNotes = useMemo(
    () => extendNotesForHolds(authoredComp.notes, holds),
    [authoredComp.notes, holds],
  );

  // Arrange is a guitar realization layered over the authored harmony. Build
  // it from the same expanded performance form as playback so repeats,
  // endings and detached codas retain their correct absolute beat positions.
  // Bass generation deliberately continues to read playbackOptimized below:
  // a displayed B♭13 remains B♭7 harmony with G realized on top.
  const playbackArrangement = useMemo(() => {
    if (!arrangePlayback) return null;
    const song = chartToSong({ ...chart, measures: playbackFlat });
    const voiced = buildArrangementVoicings(song, {
      stringSet,
      startingInversion,
      avoidB9,
      freeStringSet,
      voicingFamily,
      alternativeShapes,
      preferredAlternativeIds,
      density: chordMelodyDensity,
      pins: pins?.['melody.guitar'],
      pinKeyOf,
    });
    if (voiced.length === 0) return null;
    // Under every-note each event is one melody note's own attack, and the
    // carried harmony is written into the chord-less bars by the sequence
    // itself, so the carried-span stretch below would only make the first
    // attack ring over the ones after it.
    if (chordMelodyDensity === 'every-note') return voiced;
    // Arrangement grips realize the melody but share the source harmony's
    // sounding span. In particular, a chord carried through an otherwise
    // empty measure must keep comping there; arrangementSequence only sees
    // the authored chord's own bar and cannot infer that cross-bar duration.
    const carriedDurations = new Map(
      (playbackOptimized ?? []).map((chord) => [
        `${chord.barIndex}:${chord.beat}`,
        chord.durationBeats,
      ]),
    );
    return voiced.map((chord) => {
      const durationBeats = carriedDurations.get(`${chord.barIndex}:${chord.beat}`);
      return durationBeats == null || durationBeats === chord.durationBeats
        ? chord
        : { ...chord, durationBeats };
    });
  }, [
    arrangePlayback,
    chart,
    playbackFlat,
    playbackOptimized,
    stringSet,
    startingInversion,
    avoidB9,
    freeStringSet,
    voicingFamily,
    alternativeShapes,
    preferredAlternativeIds,
    chordMelodyDensity,
    pins,
    pinKeyOf,
  ]);

  // Melody track: the tune's melody over the PLAYBACK form (repeats, voltas
  // and detached codas included — flatten clones melody per pass). Tick
  // timing converts to absolute in-form beats; tie chains merge into one
  // sounding note (same pitch, seamless join) so the far side of a tie
  // never re-strikes.
  const melodyNotes = useMemo(() => {
    const performedMelody = withPartPlaybackRules(melodyPart, playbackFlat, playedWritten);
    const notes = chartMelodyPlayback(performedMelody, playbackBarStartBeats);
    return extendNotesForHolds(notes, holds).map((note) => {
      if (!note.expression || !holds?.length) return note;
      return {
        ...note,
        expression: note.expression.map((point) => ({
          ...point,
          beatOffset:
            point.beatOffset +
            holds.reduce(
              (extra, hold) =>
                extra +
                Math.max(
                  0,
                  Math.min(note.beat + point.beatOffset, hold.end) -
                    Math.max(note.beat, hold.start),
                ) *
                  (HOLD_STRETCH - 1),
              0,
            ),
        })),
      };
    });
  }, [melodyPart, playbackFlat, playbackBarStartBeats, playedWritten, holds]);
  const hasMelody = melodyNotes.length > 0;

  // Playhead: map the current playback beat to the active flattened bar.
  const playBeat = usePlaybackBeat();
  const currentPass = usePlaybackPass();
  // Repeats: a numeric count (1–16) plus an ∞ override (repeatForm) that loops
  // until Stop. The chip shows the live pass while playing. −/+ and the slider
  // set the count and clear ∞; the ∞ button toggles the override.
  const repeatCount = chart.repeats ?? 1;
  const passTotal = playbackPassStarts?.length ?? repeatCount;
  const repeatTotal: number | string = repeatForm ? '∞' : passTotal;
  // Practice speed: playback runs at a % of the arrangement tempo. The
  // progressive trainer overrides the manual % per form pass (pass 1 before
  // playback starts, so Play begins at fromPct).
  const activePct = trainer.enabled
    ? trainerPctForPass(Math.max(1, currentPass || 1), trainer)
    : speedPct;
  const effectiveTempo = Math.max(20, Math.round((tempo * activePct) / 100));
  const bpmForPass = useCallback(
    (pass: number) => {
      const pct = trainer.enabled ? trainerPctForPass(pass, trainer) : speedPct;
      return Math.max(20, Math.round((tempo * pct) / 100));
    },
    [tempo, trainer, speedPct],
  );
  const setRepeats = useCallback(
    (n: number) => {
      setRepeatForm(false);
      setChartRepeats(Math.max(1, Math.round(n)));
    },
    [setRepeatForm, setChartRepeats],
  );
  const playbackPassIndex = playbackPassStarts
    ? loopingPassPosition(
        currentPass || 1,
        playbackPassStarts.length,
        loopStartPassIndex(playbackPassStarts, loopStartBeat),
      ).passIndex
    : 0;
  const playingIndex = useMemo(() => {
    if (playBeat < 0) return -1;
    // A pre-expanded playback form (arrangement policy, detached coda) has its
    // own bar grid, and the player emits per-pass display beats — rebuild the
    // absolute position from the logical pass starts before mapping.
    if (detachedPlayback && playbackPassStarts && playbackPassStarts.length > 0) {
      return barAtBeat(playbackBarStartBeats, playbackPassStarts[playbackPassIndex] + playBeat);
    }
    // A looped bar range (and any other pre-expanded form without a pass
    // grid) is its own clock starting at beat 0 — map onto THAT grid, or
    // the playhead stays on the written top while the band plays the slice.
    if (detachedPlayback) return barAtBeat(playbackBarStartBeats, playBeat);
    return barAtBeat(barStartBeats, playBeat);
  }, [
    playBeat,
    barStartBeats,
    detachedPlayback,
    playbackPassStarts,
    playbackBarStartBeats,
    playbackPassIndex,
  ]);
  // What the band is doing now that the sheet does not say (#415).
  const statusLine = useMemo(
    () =>
      playbackStatus({
        barIndex: playingIndex,
        barBeat: (detachedPlayback ? playbackBarStartBeats : barStartBeats)[playingIndex] ?? 0,
        pass: Math.max(1, currentPass || 1),
        passes: passTotal,
        kinds: playbackPassStarts ? playbackForm.passKinds : undefined,
        ending: loopingRegion ? undefined : playbackForm.endingMarks,
        ritardando,
        hold:
          endingAvailable && ending.holdLast
            ? lastBarsWindow(playbackFlat, chart.timeSignature, 1)
            : undefined,
        trainerPct: trainer.enabled ? activePct : null,
      }),
    [
      playingIndex,
      detachedPlayback,
      playbackBarStartBeats,
      barStartBeats,
      currentPass,
      passTotal,
      playbackPassStarts,
      playbackForm,
      loopingRegion,
      ritardando,
      endingAvailable,
      ending.holdLast,
      playbackFlat,
      chart.timeSignature,
      trainer.enabled,
      activePct,
    ],
  );
  // Beats INTO the current bar. The subtraction must use the same grid that
  // produced playingIndex: mixing the per-pass play beat with the written grid
  // went negative from pass 2 of a pre-expanded form and clamped the score's
  // playhead line to one movement per bar.
  const playingBarBeat = useMemo(() => {
    if (playingIndex < 0) return 0;
    if (detachedPlayback && playbackPassStarts && playbackPassStarts.length > 0) {
      return Math.max(
        0,
        playbackPassStarts[playbackPassIndex] +
          playBeat -
          (playbackBarStartBeats[playingIndex] ?? 0),
      );
    }
    if (detachedPlayback) {
      return Math.max(0, playBeat - (playbackBarStartBeats[playingIndex] ?? 0));
    }
    return Math.max(0, playBeat - (barStartBeats[playingIndex] ?? 0));
  }, [
    playingIndex,
    detachedPlayback,
    playbackPassStarts,
    playbackBarStartBeats,
    barStartBeats,
    playBeat,
    playbackPassIndex,
  ]);
  // The score shows one pass, so a cycled playhead wraps back onto it per pass
  // (pickup-aware: cycled passes after the first omit the pickup bar, so a
  // plain modulo lagged the highlight a bar per pass — singlePassIndexOf is
  // the same recovery playbackOccurrences uses). Pre-expanded forms index
  // their own measures; repeated bars share ids with the written form, so the
  // sheet highlight keeps working either way.
  const playingMeasureId =
    playingIndex < 0
      ? null
      : detachedPlayback
        ? (playbackFlat[playingIndex]?.id ?? null)
        : singleFlat.length > 0
          ? (singleFlat[singlePassIndexOf(playingIndex, singleFlat)]?.id ?? null)
          : null;
  // How many times the playing bar has already sounded in the current pass
  // (0 = its first time round). A pre-expanded form's repeated bars share
  // their written id, so the id says WHICH bar but not which time round —
  // and a view drawing each time round as its own cell (the unrolled voicings
  // sheet) needs both. Counted per pass because that sheet is one literal
  // pass: each chorus of an itinerary reads through it from the top.
  const playingOccurrence = useMemo(() => {
    const id = detachedPlayback && playingIndex >= 0 ? playbackFlat[playingIndex]?.id : undefined;
    if (id === undefined) return 0;
    let from = 0;
    if (playbackPassStarts && playbackPassStarts.length > 0) {
      from = Math.max(0, barAtBeat(playbackBarStartBeats, playbackPassStarts[playbackPassIndex]));
    }
    let seen = 0;
    for (let i = from; i < playingIndex; i++) if (playbackFlat[i].id === id) seen += 1;
    return seen;
  }, [
    detachedPlayback,
    playingIndex,
    playbackFlat,
    playbackPassStarts,
    playbackBarStartBeats,
    playbackPassIndex,
  ]);

  // --- the start cursor ------------------------------------------------------
  // A tap on a bar sets the bar Play starts from and, while the band plays,
  // takes the band there (playCursor.ts decides which time through a repeat
  // a tap means). Session-only, and a new tune starts from the top.
  const playCursor = useStore((s) => s.playCursor);
  const setPlayCursor = useStore((s) => s.setPlayCursor);
  const anacrusis = chart.measures[0]?.pickupBeats != null;
  const cursorWritten = useMemo(
    () => (playCursor ? score.form.findIndex((measure) => measure.id === playCursor) : -1),
    [playCursor, score.form],
  );
  // Where Play starts: the first time the band plays the cursor's bar. Inside
  // an armed loop the loop is the form, so a cursor outside it starts the loop
  // from its top.
  const cursorStartBeat = useMemo(() => {
    if (cursorWritten < 0) return undefined;
    const index = playedOccurrence(playedWritten, cursorWritten);
    return index < 0 ? undefined : playbackBarStartBeats[index];
  }, [cursorWritten, playedWritten, playbackBarStartBeats]);
  const startCursor =
    cursorWritten < 0 || !playCursor
      ? null
      : { measureId: playCursor, bar: cursorWritten + (anacrusis ? 0 : 1) };
  const seekToBar = useCallback(
    (sheetMeasureId: string) => {
      const id = writtenMeasureId(sheetMeasureId);
      const written = score.form.findIndex((measure) => measure.id === id);
      if (written < 0) return;
      // The first bar (or the anacrusis) is the top: no cursor to show.
      setPlayCursor(written <= (anacrusis ? 1 : 0) ? null : id);
      const state = useStore.getState();
      // A bar outside the armed loop ends the loop. The band, if playing,
      // goes on through the tune from the bar (the loop-change effect below
      // restarts it, and Play reads the cursor).
      if (state.passage) {
        const inLoop = passageMeasureIds(
          score.form.map((measure) => measure.id),
          state.passage,
        ).includes(id);
        if (!inLoop) {
          state.clearPassage();
          return;
        }
      }
      if (transport === 'idle') return;
      let passStart = 0;
      let passEnd = playedWritten.length;
      if (detachedPlayback && playbackPassStarts && playbackPassStarts.length > 0) {
        passStart = Math.max(
          0,
          barAtBeat(playbackBarStartBeats, playbackPassStarts[playbackPassIndex]),
        );
        const next = playbackPassStarts[playbackPassIndex + 1];
        if (next !== undefined) passEnd = barAtBeat(playbackBarStartBeats, next);
      }
      const index = playedOccurrence(playedWritten, written, {
        index: playingIndex,
        passStart,
        passEnd,
      });
      if (index < 0) return;
      getChordPlayer().seekToBeat(playbackBarStartBeats[index]);
    },
    [
      score.form,
      setPlayCursor,
      anacrusis,
      transport,
      playedWritten,
      detachedPlayback,
      playbackPassStarts,
      playbackBarStartBeats,
      playbackPassIndex,
      playingIndex,
    ],
  );
  const clearStartCursor = useCallback(() => setPlayCursor(null), [setPlayCursor]);

  const beatsPerBar = chart.timeSignature[0];

  // --- written Feel cues (#174) ---------------------------------------------
  // A tune whose feel changes during the form resolves per onset instead of
  // through the single global switch. Everything here is INERT unless the
  // form actually carries cues and the player is following them, so the
  // overwhelming majority of tunes take exactly the path they did before.
  // SECTIONS, when turned on, take the place of written cues: every played bar
  // is labelled with its section (sectionPlan.ts) and each label plays its own
  // band. A playback choice, so it wins over what the score writes.
  const sections = useMemo(
    () => mergeSections(houseSections, ownSections),
    [houseSections, ownSections],
  );
  const sectionStarts = useMemo(
    () => (sections.on ? (sections.starts ?? detectSections(chart)?.starts ?? null) : null),
    [sections, chart],
  );
  // The spans of a form (the played one, or the sheet's): each bar labelled
  // with its section, or with the written feel it carries.
  const spansOf = useCallback(
    (measures: typeof playbackFlat, written: (number | undefined)[], barStarts: number[]) => {
      if (sectionStarts) {
        return feelSpans(
          sectionCueMeasures(written, sectionStarts),
          barStarts,
          chart.timeSignature,
          pickupBeats,
        );
      }
      return followFeelCues && hasFeelCues(measures)
        ? feelSpans(measures, barStarts, chart.timeSignature, pickupBeats)
        : null;
    },
    [sectionStarts, followFeelCues, chart.timeSignature, pickupBeats],
  );
  const cueSpans = useMemo(
    () => spansOf(playbackFlat, playedWritten, playbackBarStartBeats),
    [spansOf, playbackFlat, playedWritten, playbackBarStartBeats],
  );
  const sheetCueSpans = useMemo(
    () => spansOf(flat, sheetWritten, barStartBeats),
    [spansOf, flat, sheetWritten, barStartBeats],
  );

  /**
   * How this tune realizes each written Feel. An unassigned Feel falls back
   * to the live band rather than silence — a cue nobody has mapped yet should
   * still play the tune, and the picker shows it as unassigned.
   */
  // Your own assignments over the house default's (the owner's, saved for
  // everyone): both are choices somebody made for THIS tune.
  const ownFeels = currentTuneKey ? allTuneFeels[currentTuneKey] : undefined;
  const tuneFeels = useMemo(
    () => (houseFeels || ownFeels ? { ...houseFeels, ...ownFeels } : undefined),
    [houseFeels, ownFeels],
  );
  const feelBands = useMemo(() => {
    const live: StyleBand = {
      bassline,
      bassSolo,
      bassStyle,
      bassFeel,
      bassFollowForm,
      bassSwing,
      swingFeel,
      swingWithKit,
      melodyPlacement,
      melodyPhrasing,
      bassPlacement,
      bassAccent,
      bassSkips,
      compPlacement,
      swingOffset,
      melodySwingOffset,
      compSwingOffset,
      bassSwingOffset,
      bassGhosts,
      bassAnticipate,
      bassTriplets,
      bassAmount,
      compInstrument,
      compPianoFamily,
      compStyle,
      compAmount,
      drums,
      drumLoopKey,
    };
    // Precedence: the user's own assignment (then the house default's), then
    // the catalogue's curated default for this score, then the live band. A tune nobody has arranged
    // still plays; a tune the user HAS arranged is never overridden by a
    // shipped default.
    const defaults = catalogueFeelBands(score.id, live);
    // Sections read their own bands; written feels read the tune's feel
    // mapping, then the catalogue's default.
    const assigned = (feel: string): StyleBand | undefined =>
      sectionStarts ? sections.bands?.[feel] : tuneFeels?.[feel];
    const resolve = (feel: string | null): StyleBand => {
      const groove =
        (feel && (assigned(feel) ?? (sectionStarts ? undefined : defaults[feel]))) || live;
      // A cue changes REALIZATION, never MIXER STATE (#174). Crossing into a
      // Latin section must not un-mute a kit the player switched off, or
      // re-enable a bass line they silenced — those are performance controls
      // the human owns, and a groove has no business overriding them.
      //
      // Enforced HERE rather than at each use site so the rule is structural:
      // every consumer of resolve() inherits it, including ones not yet
      // written. The groove may still choose silence within an enabled
      // channel (a section with no kit is a musical choice); it just cannot
      // turn a channel back on.
      // A band stored before the feel fields existed plays its own kind's
      // defaults (bandFeel.ts), never undefined placements.
      return withBandFeel({
        ...groove,
        bassline: bassline && groove.bassline,
        bassSolo,
        drums: drums && groove.drums,
      });
    };
    // The band a label plays in place of the tune's, if any: what the band
    // controls show and edit for that label (components/bandScope.ts). A band
    // stored before the feel fields existed shows its own kind's defaults, as
    // it plays them.
    const own = (feel: string): StyleBand | undefined => {
      const band = assigned(feel) ?? (sectionStarts ? undefined : defaults[feel]);
      return band && withBandFeel(band);
    };
    return { live, resolve, assigned, own };
  }, [
    tuneFeels,
    sectionStarts,
    sections,
    score.id,
    bassline,
    bassSolo,
    bassStyle,
    bassFeel,
    bassFollowForm,
    bassSwing,
    swingFeel,
    swingWithKit,
    melodyPlacement,
    melodyPhrasing,
    bassPlacement,
    bassAccent,
    bassSkips,
    compPlacement,
    swingOffset,
    melodySwingOffset,
    compSwingOffset,
    bassSwingOffset,
    bassGhosts,
    bassAnticipate,
    bassTriplets,
    bassAmount,
    compInstrument,
    compPianoFamily,
    compStyle,
    compAmount,
    drums,
    drumLoopKey,
  ]);

  // How the band is split, for the controls that show and edit one band at a
  // time (components/bandScope.ts): the tune's sections while By section is on,
  // else the written feels while the band follows them.
  const writtenFeelLabels = useMemo(() => feelsInScore(chart.measures), [chart.measures]);
  const bandSplit = useMemo<BandSplit | null>(() => {
    const labels = sectionStarts
      ? [...new Set(sectionStarts.map((start) => start.label))]
      : followFeelCues
        ? writtenFeelLabels
        : [];
    if (!labels.length) return null;
    return { kind: sectionStarts ? 'sections' : 'feels', labels, ownBand: feelBands.own };
  }, [sectionStarts, followFeelCues, writtenFeelLabels, feelBands]);
  // The label the band is playing, for the status strip and the scope row.
  const playingBandLabel = useMemo(() => {
    if (!bandSplit || playingIndex < 0) return null;
    const spans = detachedPlayback ? cueSpans : sheetCueSpans;
    const beat = (detachedPlayback ? playbackBarStartBeats : barStartBeats)[playingIndex];
    return spans && beat !== undefined ? feelAtBeat(spans, beat) : null;
  }, [
    bandSplit,
    playingIndex,
    detachedPlayback,
    cueSpans,
    sheetCueSpans,
    playbackBarStartBeats,
    barStartBeats,
  ]);

  // --- instruments and melody per span ---------------------------------------
  // A span's band carries its comp instrument and piano voicings, so a trio
  // can comp the A sections with the left hand under the piano melody and the
  // bridge with both hands. Written feels read the same way: their bands are
  // bands (a catalogue default follows the live instrument, since grooves carry
  // none).
  const compPlanIn = useCallback(
    (spans: typeof cueSpans) => (beat: number) => {
      const band = spans ? feelBands.resolve(feelAtBeat(spans, beat)) : feelBands.live;
      return {
        instrument: band.compInstrument,
        family: effectivePianoFamily(band.compInstrument, band.compPianoFamily),
      };
    },
    [feelBands],
  );
  /** Where a section rests the written melody (it never sounds one the mixer muted). */
  const melodyRestsIn = useCallback(
    (spans: typeof cueSpans) => {
      if (!spans || !sectionStarts || !sections.melody) return undefined;
      if (!Object.values(sections.melody).includes(false)) return undefined;
      return (beat: number) => !sectionPlaysMelody(sections, feelAtBeat(spans, beat));
    },
    [sectionStarts, sections],
  );
  const melodyRestsAt = useMemo(() => melodyRestsIn(cueSpans), [melodyRestsIn, cueSpans]);
  const sheetMelodyRestsAt = useMemo(
    () => melodyRestsIn(sheetCueSpans),
    [melodyRestsIn, sheetCueSpans],
  );
  /** The written line as the band plays it: silent in a section that rests it. */
  const bandMelody = useMemo(
    () => (melodyRestsAt ? melodyNotes.filter((note) => !melodyRestsAt(note.beat)) : melodyNotes),
    [melodyNotes, melodyRestsAt],
  );
  // Whether any span comps on piano: the walk runs, and the samples warm.
  const pianoComp = useMemo(
    () =>
      (cueSpans ? cueSpans.map((span) => feelBands.resolve(span.feel)) : [feelBands.live]).some(
        (band) => band.compInstrument !== 'guitar',
      ),
    [cueSpans, feelBands],
  );

  // Warm the Salamander samples the moment the comp channel turns to piano,
  // so Play doesn't pay the fetch: playArrangement still awaits the load (the
  // drum-loop rule — a cold decode must not change the voice mid-form), this
  // just makes that await a no-op in the common case. Piano DIAGRAMS warm the
  // same way: clicking a keyboard strip plays through the piano timbre.
  useEffect(() => {
    if (
      (pianoComp ||
        diagramInstrument === 'piano' ||
        leadSound === 'piano' ||
        (melodyChannel && melodyInstrument === 'piano')) &&
      audioEnabled
    ) {
      void getChordPlayer().preloadPianoSamples();
    }
  }, [pianoComp, diagramInstrument, leadSound, melodyChannel, melodyInstrument, audioEnabled]);

  /** Comp style per span; the figures are laid per segment on the global grid. */
  const compSegments = useMemo((): CompSegment[] | undefined => {
    if (!cueSpans) return undefined;
    return cueSpans.map((span) => {
      const band = feelBands.resolve(span.feel);
      return {
        fromBeat: span.fromBeat,
        style: band.compStyle,
        amount: band.compAmount,
        // A Latin span's interactive comping quotes repetitive figures — the
        // same bassStyle signal that already drives the tumbao and straight
        // time for that stretch.
        feel: compFeelForBand(band),
      };
    });
  }, [cueSpans, feelBands]);

  const swingAt = useMemo(() => {
    if (!cueSpans) return undefined;
    return (beat: number) => {
      const band = feelBands.resolve(feelAtBeat(cueSpans, beat));
      return band.bassSwing;
    };
  }, [cueSpans, feelBands]);
  // How each span swings: a section's band carries its own feel. A band
  // stored before the feel existed has none, and the player then swings the
  // triplet eighth it always did.
  const swingFeelAt = useMemo(() => {
    if (!cueSpans) return undefined;
    return (beat: number) => feelBands.resolve(feelAtBeat(cueSpans, beat)).swingFeel;
  }, [cueSpans, feelBands]);
  // Whether each span's triplet eighth follows its kit (swingFeel.ts).
  const swingWithKitAt = useMemo(() => {
    if (!cueSpans) return undefined;
    return (beat: number) => feelBands.resolve(feelAtBeat(cueSpans, beat)).swingWithKit;
  }, [cueSpans, feelBands]);
  // Where each part sits against the kit: the live band's, or each span's.
  const placement = useMemo<FeelPlacement>(
    () => ({ melody: melodyPlacement, bass: bassPlacement, comp: compPlacement }),
    [melodyPlacement, bassPlacement, compPlacement],
  );
  const placementAt = useMemo(() => {
    if (!cueSpans) return undefined;
    return (beat: number): FeelPlacement => {
      const band = feelBands.resolve(feelAtBeat(cueSpans, beat));
      return { melody: band.melodyPlacement, bass: band.bassPlacement, comp: band.compPlacement };
    };
  }, [cueSpans, feelBands]);
  // How far past the kit the band swings, and each part past the band (under
  // the Kit choice; swingFeel.ts `kitSwingRatio`): the live band's, or each
  // span's.
  const swingOffsets = useMemo<SwingOffsets>(
    () => ({
      band: swingOffset,
      melody: melodySwingOffset,
      comp: compSwingOffset,
      bass: bassSwingOffset,
    }),
    [swingOffset, melodySwingOffset, compSwingOffset, bassSwingOffset],
  );
  const swingOffsetsAt = useMemo(() => {
    if (!cueSpans) return undefined;
    return (beat: number): SwingOffsets => {
      const band = feelBands.resolve(feelAtBeat(cueSpans, beat));
      return {
        band: band.swingOffset,
        melody: band.melodySwingOffset,
        comp: band.compSwingOffset,
        bass: band.bassSwingOffset,
      };
    };
  }, [cueSpans, feelBands]);

  // The walking bass line for the whole form, regenerated when the chart, the
  // chosen style/feel, or the bar grid changes. Drives both playback and the
  // on-screen analysis readout.
  const bassInput = useMemo(() => {
    if (!playbackOptimized) return [];
    return playbackOptimized.map((chord) => ({
      rootPc: pitchClassOf(chord.root),
      chordType: chord.chordType,
      // A slash chord names its own bottom note; the bass is what plays it.
      ...(chord.bass ? { bassPc: pitchClassOf(chord.bass) } : {}),
      startBeat:
        (playbackBarStartBeats[chord.barIndex] ?? chord.barIndex * beatsPerBar) + (chord.beat - 1),
      durationBeats: chord.durationBeats,
    }));
  }, [playbackOptimized, playbackBarStartBeats, beatsPerBar]);

  /**
   * Per-chord bass realization for a cued form. Resolved INSIDE one walk, so
   * the line keeps its prior note and its real next chord across a boundary —
   * a Latin→Swing change swaps the figure, it does not restart the bass.
   */
  /**
   * The heads-in-2 / solos-in-4 convention (bassFollowForm): under the
   * head/solos/head itinerary the plan's pass kinds map each chord's onset to
   * its pass's feel. Undefined outside the policy — the plan only carries
   * kinds when the itinerary is in force.
   */
  const bassFormFeelAt = useMemo(() => {
    if (!bassFollowForm || !playbackForm.passKinds || !playbackPassStarts) return undefined;
    return formFeelAt(playbackPassStarts, playbackForm.passKinds);
  }, [bassFollowForm, playbackForm.passKinds, playbackPassStarts]);

  const bassStyleAt = useMemo(() => {
    if (!cueSpans && !bassFormFeelAt) return undefined;
    return (startBeat: number) => {
      const band = cueSpans ? feelBands.resolve(feelAtBeat(cueSpans, startBeat)) : undefined;
      // The convention layers ON TOP of a cued groove's feel: the pass says 2
      // or 4, the cue still picks the style (a latin span's tumbao ignores
      // feel entirely, so nothing fights).
      const formFeel = bassFormFeelAt?.(startBeat);
      return {
        ...(band ? { style: band.bassStyle, feel: band.bassFeel } : {}),
        ...(formFeel ? { feel: formFeel } : {}),
      };
    };
  }, [cueSpans, feelBands, bassFormFeelAt]);

  /** Beats in a latin span: a tumbao must not be pushed or ghosted. */
  const skipEmbellishAt = useMemo(() => {
    if (!cueSpans) return undefined;
    return (beat: number) => feelBands.resolve(feelAtBeat(cueSpans, beat)).bassStyle === 'latin';
  }, [cueSpans, feelBands]);

  /** Where a device may not land: those latin spans, an N.C. break and the
   *  anacrusis. A root pushed into a break is dropped with it by
   *  confineBassToChords, and the player sounds no bass before the first full
   *  bar, so either way the chord after would lose its downbeat root. */
  const embellishSkipAt = useMemo(() => {
    const inHarmony = (beat: number) =>
      bassInput.some(
        (c) => beat >= c.startBeat - 1e-3 && beat < c.startBeat + c.durationBeats - 1e-3,
      );
    return (beat: number) =>
      beat < pickupBeats - 1e-3 || !inHarmony(beat) || (skipEmbellishAt?.(beat) ?? false);
  }, [bassInput, pickupBeats, skipEmbellishAt]);

  const bassLine = useMemo(
    () =>
      confineBassToChords(
        generateWalkingBass(bassInput, { style: bassStyle, feel: bassFeel, styleAt: bassStyleAt }),
        bassInput,
      ),
    [bassInput, bassStyle, bassFeel, bassStyleAt],
  );

  // Playback adds rhythmic feel (anticipations / ghost skips) on top of the
  // visual line; swing is applied by the player to whatever offbeats result.
  // The latin tumbao already IS a rhythm — jazz-time devices would wreck it.
  const embellish = useCallback(
    (line: typeof bassLine, salt: number) =>
      // A wholly-latin tune opts out entirely; a MIXED form opts out per span
      // (skipEmbellishAt), so its swing sections still get jazz time.
      // Devices lead INTO a target and sound before it — across an N.C. break
      // that puts them in the break, so the line is confined to its chords
      // again afterwards (a gapless form comes back untouched).
      confineBassToChords(
        bassStyle === 'latin' && !skipEmbellishAt
          ? line
          : embellishBassLine(line, {
              ghosts: bassGhosts,
              anticipate: bassAnticipate,
              triplets: bassTriplets,
              skips: bassSkips,
              amount: bassAmount,
              // The tune's tempo, not the practice speed: the line is the
              // tune's, and a trainer ramp must not rewrite it every pass.
              bpm: tempo,
              salt,
              skipAt: embellishSkipAt,
            }),
        bassInput,
      ),
    [
      bassStyle,
      bassGhosts,
      bassAnticipate,
      bassTriplets,
      bassSkips,
      bassAmount,
      tempo,
      skipEmbellishAt,
      embellishSkipAt,
      bassInput,
    ],
  );
  // The generated line leans on the beats its band's accent names (2 and 4
  // by default for a swinging band, even for a straight one); a written part
  // keeps the dynamics it was written with.
  const accent = useCallback(
    (notes: BassTrackNote[]) =>
      accentBassLine(notes, beatsPerBar, pickupBeats, (beat) =>
        cueSpans ? feelBands.resolve(feelAtBeat(cueSpans, beat)).bassAccent : bassAccent,
      ),
    [beatsPerBar, pickupBeats, cueSpans, feelBands, bassAccent],
  );
  // A tumbao is a straight figure whatever the band swings: its notes play
  // where they are written while the melody and comp follow the swing feel.
  const straightenTumbao = useCallback(
    (notes: BassTrackNote[]): BassTrackNote[] => {
      const latinAt = skipEmbellishAt ?? (() => bassStyle === 'latin');
      return notes.map((note) => (latinAt(note.beat) ? { ...note, swing: false as const } : note));
    },
    [skipEmbellishAt, bassStyle],
  );
  // A bass playing the written melody IS the bass in the choruses it plays
  // in: the generated line rests there and walks in the rest (a solo chorus
  // carries no melody). A muted melody leaves nobody on bass, so the band's
  // line plays throughout.
  const bassMelodySpans = useMemo(
    () =>
      melodyInstrument === 'bass' && melodyChannel
        ? melodyPassSpans(playbackPassStarts, formBeats, bandMelody)
        : [],
    [melodyInstrument, melodyChannel, playbackPassStarts, formBeats, bandMelody],
  );
  const restUnderBassMelody = useCallback(
    (notes: BassTrackNote[]) => restGeneratedIn(notes, bassMelodySpans),
    [bassMelodySpans],
  );
  const bassPlayback = useMemo(
    () =>
      thinBassForHolds(
        mergeGeneratedWithAuthored(
          restUnderBassMelody(straightenTumbao(accent(embellish(bassLine, 0)))),
          authoredBass,
        ),
        holds,
        bassInput,
      ),
    [
      embellish,
      accent,
      straightenTumbao,
      restUnderBassMelody,
      bassLine,
      authoredBass,
      holds,
      bassInput,
    ],
  );

  // A few per-pass variants so a repeated form isn't note-for-note identical:
  // variant 0 IS the canonical line (matches the on-screen readout); later
  // variants scatter alternate chord-tone choices and fill placements. The
  // player rotates through them per loop pass.
  const bassVariants = useMemo(() => {
    if (bassInput.length === 0) return [];
    return Array.from({ length: 3 }, (_, k) =>
      thinBassForHolds(
        mergeGeneratedWithAuthored(
          restUnderBassMelody(
            straightenTumbao(
              accent(
                embellish(
                  k === 0
                    ? bassLine
                    : generateWalkingBass(bassInput, {
                        style: bassStyle,
                        feel: bassFeel,
                        variation: k,
                        styleAt: bassStyleAt,
                      }),
                  k * 0.37,
                ),
              ),
            ),
          ),
          authoredBass,
        ),
        holds,
        bassInput,
      ),
    );
  }, [
    bassInput,
    bassLine,
    embellish,
    accent,
    straightenTumbao,
    restUnderBassMelody,
    bassStyle,
    bassFeel,
    bassStyleAt,
    authoredBass,
    holds,
  ]);

  // The band swings as this tune's swing feel says (the presets that play
  // straight, bossa and the rest, carry it off). The tumbao stays straight on
  // its own: straightenTumbao above.
  const effectiveSwing = bassSwing;

  /**
   * The piano realization of the same chords, when the comp channel is a
   * keyboard (#236). It rides ALONGSIDE the guitar grips rather than replacing
   * them: the sheet still shows fingerings, the comping generator still lays
   * its figures on the same events, and only the timbre and the sounding
   * pitches change. A tune with no piano answer (voiceLeadPiano returns null)
   * simply keeps playing on guitar rather than falling silent.
   */
  const pianoVoiced = useMemo(() => {
    if (!pianoComp) return null;
    const chords = playbackArrangement ?? playbackOptimized;
    if (!chords || chords.length === 0) return null;
    const onsetOf = (chord: (typeof chords)[number]) =>
      (playbackBarStartBeats[chord.barIndex] ?? chord.barIndex * beatsPerBar) + (chord.beat - 1);
    const planAt = compPlanIn(cueSpans);
    // CHORD-MELODY playback (Aug 2026): when the plan is melody-led — the
    // chords carry a written top note — the strike is the chord-melody
    // voicing, the same melody-topped stack the keyboards draw, so what
    // sounds is what shows (the guitar's grips already top the melody the
    // same way). No lifts on this path: the top is the WRITTEN note, and a
    // lift would move it.
    // The LEFT-HAND comp never takes the melody on top — the right hand is
    // already playing it — so a melody-led plan still comps in one hand there,
    // and a section resting the melody comps under it with no melody on top.
    const melodyLedPlan = chords.some(
      (chord) => 'targetTopMidi' in chord && chord.targetTopMidi != null,
    );
    // The comping walk is MELODY-AWARE: each chord carries the melody pitches
    // that sound while it rings, and the search steers around semitone rubs
    // (Stella audit, Aug 2026 — a sustained piano tone a half step off the
    // sung note read as "really dissonant"). A section resting the melody has
    // none to steer around.
    const avoid = melodyExposureByChord(chords, playbackFlat, playbackBarStartBeats);
    // A pin belongs to the PLAN the chords came from: the chord-melody plan's
    // pins when a left-hand comp walks under the melody lens, the comping
    // lane's otherwise.
    const lane = playbackArrangement ? 'melody.piano' : 'comping.piano';
    type Entry = {
      midis: number[];
      hands?: { left: number[]; right: number[] };
      liftMidis?: number[];
    };
    // Each span's texture is its own walk (pianoRuns.ts); a guitar span keeps
    // its grips. With one texture throughout this is the one walk it was.
    const voiced = voiceByRuns<
      { chord: (typeof chords)[number]; index: number },
      { family: ReturnType<typeof planAt>['family']; melodyLed: boolean },
      Entry
    >(
      chords.map((chord, index) => ({ chord, index })),
      ({ chord }) => {
        const beat = onsetOf(chord);
        const plan = planAt(beat);
        if (plan.instrument === 'guitar') return null;
        const melodyLed = plan.instrument === 'piano' && melodyLedPlan && !melodyRestsAt?.(beat);
        return { family: plan.family, melodyLed };
      },
      (plan) => (plan.melodyLed ? 'melody' : plan.family),
      (run, plan) => {
        if (plan.melodyLed) {
          return (
            voiceLeadPianoMelody(
              run.map(({ chord }) => {
                const pinnedVoicing = pianoPinnedVoicing(pins, 'melody.piano', chord);
                return {
                  root: chord.root,
                  chordType: chord.chordType,
                  displayRoot: chord.displayRoot,
                  ...(chord.bass ? { bass: chord.bass } : {}),
                  targetTopMidi: 'targetTopMidi' in chord ? chord.targetTopMidi : undefined,
                  ...(pinnedVoicing ? { pinnedVoicing } : {}),
                };
              }),
              { avoidB9 },
            )?.map((chord) => ({ midis: chord.midis, hands: chord.hands })) ?? null
          );
        }
        // Only root + quality reach the search; the union of chord shapes the
        // two playback paths produce is irrelevant to it.
        return voiceLeadPiano(
          run.map(({ chord, index }) => {
            const pinnedVoicing = pianoPinnedVoicing(pins, lane, chord);
            return {
              root: chord.root,
              chordType: chord.chordType,
              // A slash chord's left hand plays the named bass.
              ...(chord.bass ? { bass: chord.bass } : {}),
              // The chart's spelling, so an E♭ tune's voicings read in flats.
              displayRoot: chord.displayRoot,
              melodyExposure: melodyRestsAt?.(onsetOf(chord)) ? undefined : avoid[index],
              ...(pinnedVoicing ? { pinnedVoicing } : {}),
            };
          }),
          { family: plan.family, avoidB9 },
        );
      },
    );
    return voiced.some(Boolean) ? voiced : null;
  }, [
    pianoComp,
    compPlanIn,
    cueSpans,
    melodyRestsAt,
    playbackArrangement,
    playbackOptimized,
    playbackFlat,
    playbackBarStartBeats,
    beatsPerBar,
    avoidB9,
    pins,
  ]);

  /**
   * The piano realization of the SHEET's line — the same rolled/cycled
   * `optimized` array the diagram views draw. Keyed off `diagramInstrument`
   * (the Chords menu), NOT the band's `compInstrument`: what the sheet SHOWS
   * is a reading choice, what the comp channel PLAYS is the mixer's (the
   * separation the Chords menu exists for — #236 originally coupled them).
   * The VOICINGS follow the band, span by span, so a section comped with two
   * hands draws two-hand keyboards. Null on guitar diagrams, or when any chord
   * has no piano answer.
   */
  const pianoOptimized = useMemo(() => {
    if (diagramInstrument !== 'piano' || !optimized || optimized.length === 0) return null;
    const onsetOf = (chord: (typeof optimized)[number]) =>
      (barStartBeats[chord.barIndex] ?? chord.barIndex * beatsPerBar) + (chord.beat - 1);
    const planAt = compPlanIn(sheetCueSpans);
    // Melody-aware like the playback walk above, over the SHEET's own form.
    const avoid = melodyExposureByChord(optimized, flat, barStartBeats);
    const voiced = voiceByRuns(
      optimized.map((chord, index) => ({ chord, index })),
      ({ chord }) => planAt(onsetOf(chord)).family,
      (family) => family,
      (run, family) =>
        voiceLeadPiano(
          run.map(({ chord, index }) => {
            const pinnedVoicing = pianoPinnedVoicing(pins, 'comping.piano', chord);
            return {
              ...chord,
              melodyExposure: sheetMelodyRestsAt?.(onsetOf(chord)) ? undefined : avoid[index],
              ...(pinnedVoicing ? { pinnedVoicing } : {}),
            };
          }),
          { family, avoidB9 },
        ),
    );
    return voiced.every((chord) => chord !== null)
      ? (voiced as NonNullable<(typeof voiced)[number]>[])
      : null;
  }, [
    diagramInstrument,
    compPlanIn,
    sheetCueSpans,
    sheetMelodyRestsAt,
    optimized,
    flat,
    barStartBeats,
    beatsPerBar,
    avoidB9,
    pins,
  ]);

  // The playback events for the whole form. The comping track references
  // these by index, so both must come from this one array.
  const arrangementEvents = useMemo(
    () =>
      (playbackArrangement ?? playbackOptimized ?? []).map((chord, index) => ({
        fingering: chord.fingering,
        stringSet: chord.stringSet,
        startBeat:
          (playbackBarStartBeats[chord.barIndex] ?? chord.barIndex * beatsPerBar) +
          (chord.beat - 1),
        durationBeats: chord.durationBeats,
        // A written band hit needs the harmonic bass even when a syncopated
        // generated bass style has no attack on this downbeat.
        bassMidi: 36 + pitchClassOf(chord.bass ?? chord.root),
        pianoMidis: pianoVoiced?.[index]?.midis,
        // The hand split, for families that have one — the interactive comp
        // style's per-hand hits (statement = both, answer = right) strike
        // subsets of it. Families without a split leave it undefined and
        // every hit strikes the whole voicing.
        pianoHands: pianoVoiced?.[index]?.hands,
        // The lift — the nearest inversion above — for the interactive
        // style's repeated-attack climbs. Playback-only ornamentation: the
        // sheet keeps drawing the walk's voicing.
        pianoLift: pianoVoiced?.[index]?.liftMidis,
      })),
    [playbackArrangement, playbackOptimized, playbackBarStartBeats, beatsPerBar, pianoVoiced],
  );
  // What stops the transport vs what swaps LIVE. Play snapshots the FORM —
  // the pass grid, the count-in shape, the total length — into the running
  // closure, so a change to any of that (a bar added, a policy rebuild, an
  // itinerary switch) cannot be patched in: stop cleanly and let the next
  // Play take one coherent score.
  const arrangementFormShape = useMemo(
    () =>
      [
        formBeats,
        pickupBeats,
        loopStartBeat ?? '',
        (playbackPassStarts ?? []).join(','),
        ...authoredCompSpans.map((span) => `span:${span.startBeat}:${span.durationBeats}`),
        ...authoredBassSpans.map((span) => `bass-span:${span.startBeat}:${span.durationBeats}`),
        ...authoredCompNotes.map((note) => `note:${note.beat}:${note.durationBeats}`),
        ...bandCues.map(
          (cue) =>
            `cue:${cue.channel}:${cue.mode}:${cue.startBeat}:${cue.endBeat}:${cue.hitDurationBeats ?? ''}`,
        ),
      ].join('\u0000'),
    [
      authoredCompNotes,
      authoredCompSpans,
      authoredBassSpans,
      bandCues,
      formBeats,
      loopStartBeat,
      pickupBeats,
      playbackPassStarts,
    ],
  );
  // Inside a form that holds, everything about the events can move and still
  // be swapped in: their CONTENT (which grip, which piano pitches — comp
  // instrument, voicing family, engine, string set, a transposed KEY) and,
  // since the reharm audition, the CHORDS THEMSELVES, where one chord becomes
  // two. That last case moves the positions CompHit.chordIndex addresses, so
  // the hits go over in the same call — see setArrangementEvents.
  const arrangementEventShape = useMemo(
    () =>
      arrangementEvents.map((event) => `${event.startBeat}:${event.durationBeats}`).join('\u0000'),
    [arrangementEvents],
  );

  // Comping rhythm (Charleston, pushes, freestyle stabs) — playback only.
  const compHits = useMemo(() => {
    // Only an all-Held band hands the player `undefined` (its own strum per
    // chord). With cues, the SPANS decide: a live Held band still comps the
    // figures a cued groove asks for, and a Held span strikes its chords.
    if (compSegments ? compSegments.every((s) => s.style === 'held') : compStyle === 'held')
      return undefined;
    const mixedMeter = playbackFlat.some(
      (m) => m.timeSig && (m.timeSig[0] !== beatsPerBar || m.timeSig[1] !== chart.timeSignature[1]),
    );
    const compOptions = {
      beatsPerBar,
      amount: compAmount,
      mixedMeter,
      // An anacrusis offsets every barline: lay the figures from bar 1.
      barOffset: pickupBeats,
      // The interactive engine listens to the melody, follows the chorus
      // arc, and picks its vocabulary by tempo and time-feel; the other
      // styles ignore these. A latin bass is the feel signal — the same
      // seam that forces straight time for the tumbao.
      // A muted lead is normally solo practice. Supply a supportive
      // background instead of answering gaps in an unheard written line.
      melody: melodyChannel ? bandMelody : undefined,
      passStarts: playbackPassStarts,
      passKinds: playbackPassStarts ? playbackForm.passKinds : undefined,
      bpm: effectiveTempo,
      feel: compFeelForBand({ bassStyle, compStyle, drumLoopKey }),
    };
    const hits = compSegments
      ? generateSegmentedComping(
          arrangementEvents,
          { ...compOptions, style: compStyle },
          compSegments,
        )
      : generateComping(arrangementEvents, { ...compOptions, style: compStyle });
    // A catalogue arrangement can suggest Beat 2 answers in explicit written
    // regions. Preserve the continuous Interactive phrase plan and move only
    // its existing answers; never replace the band or infer a pattern from
    // slash chords. The player's rhythm choice and own Feel assignments win.
    const eligibleOccurrences =
      followFeelCues && compStyle === 'interactive'
        ? playbackOccurrences.filter((occurrence) =>
            (cueSpans ?? []).every((span, index) => {
              const end = cueSpans?.[index + 1]?.fromBeat ?? Infinity;
              if (
                end <= occurrence.startBeat ||
                span.fromBeat >= occurrence.startBeat + occurrence.beats
              )
                return true;
              return (
                !(span.feel && feelBands.assigned(span.feel)) &&
                feelBands.resolve(span.feel).compStyle === 'interactive'
              );
            }),
          )
        : [];
    const ranges = catalogueCompPatternRanges(score, eligibleOccurrences);
    const patterned = applyBeatTwoAnswers(arrangementEvents, hits, { ...compOptions, ranges });
    const sustained = sustainCompHitsForHolds(patterned, holds, arrangementEvents);
    return sustained?.filter(
      (hit) =>
        !authoredCompSpans.some(
          (span) =>
            hit.beat >= span.startBeat - 1e-3 &&
            hit.beat < span.startBeat + span.durationBeats - 1e-3,
        ),
    );
  }, [
    arrangementEvents,
    compStyle,
    compSegments,
    beatsPerBar,
    compAmount,
    playbackFlat,
    chart.timeSignature,
    pickupBeats,
    holds,
    authoredCompSpans,
    bandMelody,
    melodyChannel,
    playbackPassStarts,
    playbackForm.passKinds,
    effectiveTempo,
    bassStyle,
    drumLoopKey,
    score,
    playbackOccurrences,
    followFeelCues,
    cueSpans,
    feelBands,
  ]);

  const arrangementShapeRef = useRef({ form: arrangementFormShape, events: arrangementEventShape });
  // What the form was built from, to recognise an Ending change (see formBasis).
  const formBuiltRef = useRef({ basis: formBasis, ending });
  useEffect(() => {
    const previous = arrangementShapeRef.current;
    const built = formBuiltRef.current;
    arrangementShapeRef.current = { form: arrangementFormShape, events: arrangementEventShape };
    formBuiltRef.current = { basis: formBasis, ending };
    if (!sequencePlaying) return;
    if (prevLoopKeyRef.current !== loopKey) return;
    if (previous.form !== arrangementFormShape) {
      const endingOnly = built.basis === formBasis && built.ending !== ending;
      if (
        endingOnly &&
        getChordPlayer().setArrangementForm(arrangementEvents, liveArrangementOptionsRef.current)
      )
        return;
      getChordPlayer().stop();
      return;
    }
    // A refused swap (the player sees a mismatch these strings missed) still
    // stops rather than letting the sheet and the sound disagree.
    if (!getChordPlayer().setArrangementEvents(arrangementEvents, { compHits }))
      getChordPlayer().stop();
  }, [
    arrangementEventShape,
    arrangementFormShape,
    arrangementEvents,
    compHits,
    sequencePlaying,
    loopKey,
    formBasis,
    ending,
  ]);

  // The drum track: only loops that fit the chart's bar make sense (a loop bar
  // that's a whole multiple of the tune's does — see loopsForMeter), and the
  // player stretches the chosen loop to the arrangement's tempo.
  const meterDrumLoops = useMemo(
    () => loopsForMeter(drumLoops, beatsPerBar),
    [drumLoops, beatsPerBar],
  );
  const activeDrumLoop = useMemo(
    () =>
      drums ? (meterDrumLoops.find((l) => l.key === drumLoopKey) ?? meterDrumLoops[0]) : undefined,
    [drums, meterDrumLoops, drumLoopKey],
  );
  const drumTrack = useMemo(
    () =>
      activeDrumLoop
        ? {
            url: drumLoopURL(activeDrumLoop),
            bpm: activeDrumLoop.bpm,
            beats: activeDrumLoop.beats,
            seconds: activeDrumLoop.seconds,
            ...(activeDrumLoop.swing !== undefined ? { swing: activeDrumLoop.swing } : {}),
          }
        : null,
    [activeDrumLoop],
  );

  const drumSegments = useMemo(() => {
    if (!cueSpans) return undefined;
    return cueSpans.map((span) => {
      const band = feelBands.resolve(span.feel);
      const loop = band.drums
        ? (meterDrumLoops.find((l) => l.key === band.drumLoopKey) ?? meterDrumLoops[0])
        : undefined;
      return {
        fromBeat: span.fromBeat,
        loop: loop
          ? {
              url: drumLoopURL(loop),
              bpm: loop.bpm,
              beats: loop.beats,
              seconds: loop.seconds,
              ...(loop.swing !== undefined ? { swing: loop.swing } : {}),
            }
          : null,
      };
    });
  }, [cueSpans, feelBands, meterDrumLoops]);

  // Prepare the kit while the player is choosing a tune, groove or tempo.
  // Play shares this cache/promise, instead of starting a cold render then.
  // Debounce a dragged tempo slider so only its settled value starts work.
  useEffect(() => {
    if (!audioEnabled || sequencePlaying || formBeats <= 0) return;
    const timer = setTimeout(() => {
      void getChordPlayer().preloadArrangementDrums({
        bpm: effectiveTempo,
        bpmForPass,
        drums: drumTrack,
        drumSegments,
        drumTimeStretch,
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [
    audioEnabled,
    sequencePlaying,
    formBeats,
    effectiveTempo,
    bpmForPass,
    drumTrack,
    drumSegments,
    drumTimeStretch,
  ]);

  // What the running arrangement is fed, built once: the live push below,
  // Play, and a form swapped in mid-playback (an Ending change) all read it.
  // The written line keeps its notes; where the band swings with Swing
  // phrasing it is phrased the way a horn swings it (audio/phrasing.ts).
  const playedMelody = useMemo(() => {
    const bandAt = (beat: number) =>
      cueSpans ? feelBands.resolve(feelAtBeat(cueSpans, beat)) : feelBands.live;
    const phrased = (beat: number) => {
      const band = bandAt(beat);
      return band.bassSwing && band.melodyPhrasing === 'swing';
    };
    if (!(cueSpans ? cueSpans.some((span) => phrased(span.fromBeat)) : phrased(0))) {
      return bandMelody;
    }
    return phraseMelody(bandMelody, {
      swingsAt: phrased,
      pairAt: (beat) => swingGrid(bandAt(beat).swingFeel).pair,
      origin: pickupBeats - Math.floor(pickupBeats),
    });
  }, [bandMelody, cueSpans, feelBands, pickupBeats]);
  const liveArrangementOptions = useMemo<ArrangementOptions>(
    () => ({
      bpm: effectiveTempo,
      formBeats,
      bpmForPass,
      beatsPerBar,
      metronome,
      metronomeBeats,
      bassline,
      soloBass: bassSolo,
      bassNotes: bassPlayback,
      bassVariants,
      melody: melodyChannel && hasMelody,
      melodyInstrument,
      melodyNotes: playedMelody,
      swing: effectiveSwing,
      swingFeel,
      swingWithKit: effectiveSwing && swingWithKit,
      swingWithKitAt,
      swingOffsets,
      swingOffsetsAt,
      placement,
      placementAt,
      feel: feelTiming,
      swingAt,
      swingFeelAt,
      drumSegments,
      compHits,
      compStyle,
      authoredCompNotes,
      authoredCompSpans,
      authoredBassSpans,
      writtenBassPlays,
      bandCues,
      drums: drumTrack,
      drumTimeStretch,
      loop: repeatForm || loopingRegion,
      loopCount: playbackPassStarts ? 1 : (chart.repeats ?? 1),
      passStarts: playbackPassStarts,
      pickupBeats,
      loopStartBeat,
      holds,
      ritardando,
      barStarts: playbackBarStartBeats,
    }),
    [
      melodyChannel,
      melodyInstrument,
      hasMelody,
      playedMelody,
      swingFeel,
      feelTiming,
      effectiveTempo,
      formBeats,
      bpmForPass,
      beatsPerBar,
      metronome,
      metronomeBeats,
      bassline,
      bassSolo,
      bassPlayback,
      bassVariants,
      effectiveSwing,
      swingWithKit,
      swingWithKitAt,
      swingOffsets,
      swingOffsetsAt,
      placement,
      placementAt,
      swingAt,
      swingFeelAt,
      drumSegments,
      compHits,
      compStyle,
      authoredCompNotes,
      authoredCompSpans,
      authoredBassSpans,
      writtenBassPlays,
      bandCues,
      drumTrack,
      drumTimeStretch,
      repeatForm,
      loopingRegion,
      chart.repeats,
      playbackPassStarts,
      pickupBeats,
      loopStartBeat,
      holds,
      ritardando,
      playbackBarStartBeats,
    ],
  );
  const liveArrangementOptionsRef = useRef(liveArrangementOptions);
  liveArrangementOptionsRef.current = liveArrangementOptions;

  // Push control changes to the running arrangement so tempo, metronome, bass,
  // comping, solo, style and loop all update live mid-playback (not just on
  // the next Play). Not while a loop-range change is pending: the new slice's
  // tracks belong to events the running arrangement does not have, and the
  // restart effect on `loopKey` hands them over with those events (or stops a
  // paused band).
  useEffect(() => {
    if (!sequencePlaying) return;
    if (prevLoopKeyRef.current !== loopKey) return;
    getChordPlayer().setArrangementOptions(liveArrangementOptions);
  }, [sequencePlaying, liveArrangementOptions, loopKey]);

  const playAll = useCallback(
    (opts?: { countIn?: boolean; startMeasureId?: string; startMeasureBeat?: number }) => {
      if (formBeats <= 0 || !audioEnabled) return;
      const requestedId = opts?.startMeasureId;
      const requestedWritten = requestedId
        ? score.form.findIndex((measure) => measure.id === writtenMeasureId(requestedId))
        : -1;
      const requestedOccurrence =
        requestedWritten < 0 ? -1 : playedOccurrence(playedWritten, requestedWritten);
      const requestedStartBeat =
        requestedOccurrence < 0 ? undefined : playbackBarStartBeats[requestedOccurrence];
      const requestedOffset = Number.isFinite(opts?.startMeasureBeat)
        ? Math.max(0, (opts?.startMeasureBeat ?? 1) - 1)
        : 0;
      const occurrenceLength =
        requestedStartBeat === undefined
          ? 0
          : (playbackBarStartBeats[requestedOccurrence + 1] ?? formBeats) - requestedStartBeat;
      getChordPlayer().playArrangement(arrangementEvents, {
        ...liveArrangementOptions,
        nowPlaying: { title: chart.title, artist: chart.composer },
        countIn: opts?.countIn ?? countIn,
        countInBars,
        countInBetweenLoops,
        startBeat: requestedId
          ? (requestedStartBeat ?? 0) + Math.min(requestedOffset, occurrenceLength)
          : cursorStartBeat,
      });
    },
    [
      arrangementEvents,
      audioEnabled,
      formBeats,
      liveArrangementOptions,
      cursorStartBeat,
      score.form,
      playedWritten,
      playbackBarStartBeats,
      countIn,
      countInBars,
      countInBetweenLoops,
      chart.title,
      chart.composer,
    ],
  );

  // Dragging a new range updates the highlight on every bar; wait until the
  // gesture settles, then retarget the running loop. Clearing the range while
  // the band plays hands it the whole tune from the top — the way out of a
  // loop made by a stray drag, and the band keeps playing. Never start from idle
  // or paused — Play is what begins playback. A paused band stops instead:
  // its events and pass grid were snapshotted at Play, so Resume could only
  // replay the old form; stopped, Play starts the armed range.
  const playAllRef = useRef(playAll);
  playAllRef.current = playAll;
  useEffect(() => {
    if (prevLoopKeyRef.current === loopKey) return;
    if (transport !== 'playing') {
      prevLoopKeyRef.current = loopKey;
      if (transport === 'paused') getChordPlayer().stop();
      return;
    }
    const timer = window.setTimeout(() => {
      prevLoopKeyRef.current = loopKey;
      playAllRef.current({ countIn: false });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [loopKey, transport]);

  // Written parts can make a chordless/N.C. score playable even when there is
  // no optimised harmony arrangement from which to infer a transport.
  const showTransportBar =
    !!optimized || hasMelody || authoredBass.notes.length > 0 || authoredCompNotes.length > 0;

  // The comp and the walking bass are *generated from the chords*, so on a
  // chordless tune (a melody-only import, an all-N.C. chart) they have nothing
  // to build from and stay silent however their switches are set — unless the
  // tune carries a written part for that channel. The mixer reports this
  // instead of offering controls that cannot make a sound. (Drums need no
  // harmony, so they are unaffected.)
  const hasHarmony = !!optimized;
  const compHasSource = hasHarmony || authoredCompNotes.length > 0;
  const bassHasSource = hasHarmony || authoredBass.notes.length > 0;

  // The strip names the section or feel playing first, when the band is split:
  // which band you are hearing is what the rows and the scope row speak of.
  const bandStatusLine = useMemo(
    () => (playingBandLabel ? [scopeLabelText(playingBandLabel), ...statusLine] : statusLine),
    [playingBandLabel, statusLine],
  );

  return {
    chart,
    transport,
    sequencePlaying,
    // Form + voicings (pages build their diagram grids from these).
    singleFlat,
    flat,
    /**
     * Bars actually PLAYED. Differs from `flat` whenever the playback form is
     * pre-expanded — the head-solos-out itinerary, a detached coda — and the
     * transport reports it, because the written count alone made a policy
     * switch invisible: All Of Me kept saying "32 bars" while playing 96.
     */
    playedBars: playbackFlat.length,
    preExpanded: detachedPlayback,
    /**
     * The loop's shape under as-written repeats: written intro/verse bars
     * playing once on the way in, coda/outro bars once on the way out. Zero
     * when the whole form loops. The transport says so — a loop that quietly
     * skips the intro reads as broken unless the UI names the behaviour.
     */
    entryBars: loopingRegion ? 0 : (playbackForm.entryBars ?? 0),
    exitBars: loopingRegion ? 0 : (playbackForm.exitBars ?? 0),
    /** How the last pass finishes, and whether this playback has an end at all. */
    ending,
    patchEnding,
    endingAvailable,
    barStartBeats,
    sequence,
    optimized,
    pianoOptimized,
    /** Serializable identity for an export, bug report or benchmark. */
    voiceLeadingEngine: engineDiagnostic,
    cycle,
    cycleActive,
    // Playhead.
    playBeat,
    currentPass,
    playingIndex,
    playingBarBeat,
    playingMeasureId,
    playingOccurrence,
    // Tempo / repeats / style.
    tempo,
    activePct,
    effectiveTempo,
    bpmForPass,
    repeatCount,
    repeatTotal,
    setRepeats,
    currentStyleId,
    currentStyleLabel,
    currentStyleModified,
    // Mixer facts.
    beatsPerBar,
    hasMelody,
    melodyInstrument,
    hasHarmony,
    compHasSource,
    bassHasSource,
    meterDrumLoops,
    activeDrumLoop,
    // Transport actions.
    playAll,
    /** The bar Play starts from (a tapped bar), or null for the top. */
    startCursor,
    /** A tap on a bar: the start cursor, and while playing, the band goes there. */
    seekToBar,
    clearStartCursor,
    /**
     * Chorus skips make sense wherever the form has choruses to step between:
     * not inside a passage loop (a selection, not a chorus) or a cycled form
     * (the passes are the voice-leading's, not the tune's).
     */
    chorusSkipAvailable: !loopingRegion && !cycleActive,
    /** What is in force at the playing bar: the section or feel, chorus job, tag pass, rit., hold, trainer (#415). */
    statusLine: bandStatusLine,
    /** How the band is split by section or written feel, for the scoped band controls. */
    bandSplit,
    /** The section or feel the band is playing, when split. */
    playingBandLabel,
    /** What each pass is (head / solo / head out), for naming the choruses. */
    chorusKinds: playbackPassStarts ? playbackForm.passKinds : undefined,
    showTransportBar,
  };
}
